import { CellRenderers } from './CellRegistry';
import { satisfies } from '../binding/predicate';
import { scopesOn } from '../binding/scopes';

const COLUMN_SCOPES = scopesOn('list');

/**
 * Compiles a column layout into the column array the contact lists consume, so a list's columns are
 * declared rather than written.
 *
 * A layout entry carries everything needed to render it - `key`, `label`, `size`, the `cell` and
 * whatever that cell reads (`options`, `colors`, `caption`) - and never refers back to the
 * catalogue. `key` is a value path, so `attributes.SEGMENT` reaches into the section document.
 *
 * `visibleWhen` decides whether a column is in the list at all, read once here against the runtime
 * values the page publishes.
 *
 * @param {Array} layout - The columns to show, in order.
 * @param {Object} options - Compilation options.
 * @param {Object} options.context - Runtime values a saved layout cannot hold, read by cells.
 * @param {Object} options.renderers - Cells to add to the registry, or override in it.
 *
 * @returns {Array}
 */
export const resolveColumns = (layout, { context = {}, renderers = {} } = {}) =>
  (layout ?? [])
    .filter(({ visibleWhen }) =>
      satisfies(visibleWhen, { context }, COLUMN_SCOPES)
    )
    .map((field) => {
      const render = renderers[field.cell] ?? CellRenderers[field.cell];

      if (field.cell && !render)
        console.warn(`Unsupported cell type: ${field.cell}`);

      return {
        id: field.key,
        label: field.label,
        size: field.size,
        noCaption: field.noCaption,
        reverse: field.reverse,
        // Without a cell the list renders the resolved value as it is.
        ...(render
          ? {
              formatCallback: (value, data, translate) =>
                render(value, data, { field, context, translate }),
            }
          : {}),
      };
    });
